import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs";
import Image from "../../components/images/Image";
import Shimmer from "../AdminManagement/components/ViewAdminDetailsShimmer";
import useApi from "../../services/useApi";
import useTranslation from "../../components/CustomHooks/useTranslation";
import { adminContents } from "../AdminManagement/components/AdminManagementContent";
import config from "../../config/config";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../store";
// import { MasterDistributor, NationalID, PaperTazkera, Passport, ServiceFee, CVM } from "Components/types";
import moment from "moment";
import ToolTip from "../../components/ToolTip/ToolTip";
import { setUserUrl } from "../../store/slices/geolocationSlice";
import { useSelector } from "react-redux";



interface PhoneNumber {
    country_code: string;
    phone_number: string;
}

interface LogChange {
    field: string;
    field_path: string;
    before_value: any;
    after_value: any;
    mno_name?: string;
}

interface LogSection {
    changed: boolean;
    changes: LogChange[];
}

interface LogSections {
    [key: string]: LogSection;
}

interface LogData {
    sections: LogSections;
    timestamp: string;
}

interface LogDetails {
    id: string;
    log_id: string;
    date: string;
    action_taken: string;
    action_taken_on: string;
    initiated_by: string;
    initiated_by_location: string;
    action_taken_on_location: string;
    log_type: string;
    logs: LogData;
}

export default function ViewLogDetails() {
    const { getAPI } = useApi();
    const { notEnglish } = useTranslation(adminContents);
    const [loading, setLoading] = useState(true);
    const previousUrl = useSelector((state: RootState) => state.geolocation.previousUrl);

    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();
    const detailsRef = useRef<HTMLDetailsElement>(null);
    const isReversalLog = searchParams.get('log_type') === 'Reversal';
    const isFundTransferLog = location.pathname.includes('/fund-transfer-logs/');
    useEffect(() => {
        dispatch(setUserUrl(window.location.pathname));

        const handleClickOutside = (event: MouseEvent) => {
            if (
                detailsRef.current &&
                !detailsRef.current.contains(event.target as Node)
            ) {
                detailsRef.current.open = false;
            }
        };

        document.addEventListener("click", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, []);

    const [masterDistributorDetails, setMasterDistributor] = useState<LogDetails | null>(null);

    const cleanFieldName = (fieldName: string): string => {


        // Handle upfront service fee percentage specifically
        if (fieldName.includes('upfront_service_fee_percentage')) {
            return 'Upfront Service Fee Percentage';
        }

        // Handle bank details fields specifically
        if (fieldName.includes('bank_name')) {
            return 'Bank Name';
        }
        if (fieldName.includes('account_number')) {
            return 'Account Number';
        }
        if (fieldName.includes('bank_passbook_front_page')) {
            return 'Bank Passbook Front Page';
        }

        // Handle service fee fields specifically
        if (fieldName.includes('service_fee_type')) {
            return 'Service Fee Type';
        }
        if (fieldName.includes('service_fee_parent')) {
            return 'Parent User';
        }
        if (fieldName.includes('mno_id') || fieldName.includes('mno_name')) {
            return 'Telecom';
        }
        if (fieldName.includes('etopup_dispersive_fee_percent')) {
            return 'eTop Up Service Fee Percentage';
        }
        if (fieldName.includes('bundle_dispersive_fee_percent')) {
            return 'Bundle Activation Service Fee Percentage';
        }
        if (fieldName.includes('status')) {
            return 'Status';
        }

        // Handle CVM fields specifically
        if (fieldName.includes('from_date')) {
            return 'From Date';
        }
        if (fieldName.includes('to_date')) {
            return 'To Date';
        }
        if (fieldName.includes('target_amount')) {
            return 'Target Amount';
        }
        if (fieldName.includes('on_target_achievement_percentage')) {
            return 'On Target Achievement Percentage';
        }

        // Handle identification details fields specifically
        if (fieldName.includes('identification_type')) {
            return 'Identification Type';
        }

        // Passport fields
        if (fieldName.includes('passport_number')) {
            return 'Passport Number';
        }
        if (fieldName.includes('passport_valid_from')) {
            return 'Valid From';
        }
        if (fieldName.includes('passport_valid_to')) {
            return 'Valid Up To';
        }
        if (fieldName.includes('passport_front_page')) {
            return 'Passport Front Page';
        }

        // National ID fields
        if (fieldName.includes('national_id_number')) {
            return 'National ID Number';
        }
        if (fieldName.includes('national_id_valid_from')) {
            return 'Valid From';
        }
        if (fieldName.includes('national_id_valid_to')) {
            return 'Valid Up To';
        }
        if (fieldName.includes('mothers_name')) {
            return 'Mother\'s Name';
        }
        if (fieldName.includes('national_id_front_page')) {
            return 'National ID Front Page';
        }
        if (fieldName.includes('national_id_back_page')) {
            return 'National ID Back Page';
        }
        
        // Business details fields
        if (fieldName.includes('store_name')) {
            return 'Store Name';
        }
        if (fieldName.includes('business_type')) {
            return 'Business Type';
        }
        if (fieldName.includes('business_license_number')) {
            return 'Business License Number';
        }
        if (fieldName.includes('business_valid_from')) {
            return 'Business Valid From';
        }
        if (fieldName.includes('business_valid_to')) {
            return 'Business Valid Up To';
        }
        if (fieldName.includes('business_license_front_page')) {
            return 'Business License Front Page';
        }
        if (fieldName.includes('business_license_back_page')) {
            return 'Business License Back Page';
        }

        // Remove array indices like [0], [1], etc. from field names
        let cleaned = fieldName.replace(/\[\d+\]/g, '');

        // Remove duplicate "service_fee" prefix for service fee fields
        if (cleaned.includes('service_fee.service_fee')) {
            cleaned = cleaned.replace('service_fee.service_fee', 'service_fee');
        }

        return cleaned;
    };


    const formatValue = (value: any, fieldName: string): React.ReactNode => {
        // Handle status field for service fee - show Active/Inactive (check before early return)
        if (fieldName.includes('status')) {
            return value ? 'Active' : 'Inactive';
        }

        if (!value) return '-';

        // Handle empty arrays for service_fee_details
        if (fieldName === 'service_fee_details' && Array.isArray(value) && value.length === 0) {
            return '-';
        }

        // Handle empty arrays for CVM targets
        if (fieldName === 'targets' && Array.isArray(value) && value.length === 0) {
            return '-';
        }

        // Handle service_fee_details array with objects
        if (fieldName === 'service_fee_details' && Array.isArray(value) && value.length > 0) {
            return (
                <div className="">
                    {value.map((item: any, index: number) => (
                        <div key={index} className="mb-6 p-4 border border-gray-200 rounded-lg">
                            {value.length > 1 && (
                                <div className="text-sm text-gray-600 mb-3 font-semibold">
                                    Service Fee Details ({index + 1})
                                </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {(item.mno_name || item.mno_id) && (
                                    <div>
                                        <div className="text-sm text-gray-600">Telecom</div>
                                        <div className="mt-1 font-medium text-gray-900" data-testid="telecom">{item.mno_name || item.mno_id}</div>
                                    </div>
                                )}
                                {item.bundle_dispersive_fee_percent !== undefined && (
                                    <div>
                                        <div className="text-sm text-gray-600">Bundle Activation Service Fee Percentage</div>
                                        <div className="mt-1 font-medium text-gray-900" data-testid="bundle-activation-service-fee-percentage">{Number(item.bundle_dispersive_fee_percent).toFixed(2)}%</div>
                                    </div>
                                )}
                                {item.etopup_dispersive_fee_percent !== undefined && (
                                    <div>
                                        <div className="text-sm text-gray-600">eTop Up Service Fee Percentage</div>
                                        <div className="mt-1 font-medium text-gray-900" data-testid="etop-up-service-fee-percentage">{Number(item.etopup_dispersive_fee_percent).toFixed(2)}%</div>
                                    </div>
                                )}
                                {item.status !== undefined && (
                                    <div>
                                        <div className="text-sm text-gray-600">Status</div>
                                        <div className="mt-1 font-medium text-gray-900" data-testid="status">{item.status ? 'Active' : 'Inactive'}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        // Handle CVM targets array with objects
        if (fieldName === 'targets' && Array.isArray(value) && value.length > 0) {
            return (
                <div className="">
                    {value.map((item: any, index: number) => (
                        <div key={index} className="mb-6 p-4 border border-gray-200 rounded-lg">
                            {value.length > 1 && (
                                <div className="text-sm text-gray-600 mb-3 font-semibold">
                                    Target ({index + 1})
                                </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {item.target_amount !== undefined && (
                                    <div>
                                        <div className="text-sm text-gray-600">Target Amount</div>
                                        <div className="mt-1 font-medium text-gray-900" data-testid="target-amount">{item.target_amount}</div>
                                    </div>
                                )}
                                {item.on_target_achievement_percentage !== undefined && (
                                    <div>
                                        <div className="text-sm text-gray-600">On Target Achievement Percentage</div>
                                        <div className="mt-1 font-medium text-gray-900" data-testid="on-target-achievement-percentage">{Number(item.on_target_achievement_percentage).toFixed(2)}%</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        // Handle bank_details array with objects
        if (fieldName === 'bank_details' && Array.isArray(value) && value.length > 0) {
            return (
                <div className="">
                    {value.map((item: any, index: number) => (
                        <div key={index} className="mb-6 p-4 border border-gray-200 rounded-lg">
                            {value.length > 1 && (
                                <div className="text-sm text-gray-600 mb-3 font-semibold">
                                    Bank Details ({index + 1})
                                </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {item.bank_name && (
                                    <div>
                                        <div className="text-sm text-gray-600">Bank Name</div>
                                        <div className="mt-1 font-medium text-gray-900" data-testid="bank-name">{item.bank_name}</div>
                                    </div>
                                )}
                                {item.account_number && (
                                    <div>
                                        <div className="text-sm text-gray-600">Account Number</div>
                                        <div className="mt-1 font-medium text-gray-900" data-testid="account-number">{item.account_number}</div>
                                    </div>
                                )}
                                {item.bank_passbook_front_page && (
                                    <div>
                                        <div className="text-sm text-gray-600">Bank Passbook Front Page</div>
                                        <div className="mt-1" data-testid="bank-passbook-front-page">
                                            {formatValue(item.bank_passbook_front_page, 'file')}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        // Handle wallet_access array - show string values instead of array format
        if (fieldName === 'wallet_access' && Array.isArray(value)) {
            return value.join(', ');
        }

        // Handle business_assigned_area array with objects
        if (fieldName === 'business_assigned_area' && Array.isArray(value)) {
            return (
                <div className="">
                    {value.map((item: any, index: number) => (
                        <div key={index} className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {item.region && (
                                <div className="mb-2">
                                    <div className="text-sm text-gray-600">Region</div>
                                    <div className="mt-1 font-medium text-gray-900" data-testid="region">{item.region}</div>
                                </div>
                            )}
                            {item.provinces && Array.isArray(item.provinces) && (
                                <div className="mb-2">
                                    <div className="text-sm text-gray-600">Provinces</div>
                                    <div className="mt-1 font-medium text-gray-900" data-testid="provinces">{item.provinces.join(', ')}</div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            );
        }

        // Handle file paths
        if (typeof value === 'string' && value.includes('/') && (value.includes('uploads/') || value.includes('.jpg') || value.includes('.pdf') || value.includes('.png'))) {
            return (
                <div className={`w-full ${fieldName === 'photo' ? "sm:w-[65%]" : "sm:w-[40%]"} flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1`}>
                    <span className="more-lines">{value.split("/").pop()}</span>
                    <ToolTip ToolTipItem={value.split("/").pop() || ''} top="top-0" />
                    <Image src="file-eye" className="cursor-pointer" objectFit={'contain'} onClick={() => {
                        const fileUrl = config?.cdnURL + 'public/' + value;
                        window.open(fileUrl, "_blank");
                    }} />
                </div>
            );
        }

        // Handle secondary_phone_numbers array
        if (fieldName === 'secondary_phone_numbers' && Array.isArray(value)) {
            return value.map((phone: PhoneNumber) => `${phone.country_code} ${phone.phone_number}`).join(', ');
        }

        // Handle timestamp fields
        if (typeof value === 'string' && value.includes('T') && value.includes('Z')) {
            return moment(value).format('DD-MM-YYYY, hh:mm A');
        }

        // Handle other arrays
        if (Array.isArray(value)) {
            return JSON.stringify(value, null, 2);
        }

        // Handle specific service fee percentage fields with toFixed(2) and %
        if (fieldName.includes('bundle_dispersive_fee_percent') || fieldName.includes('etopup_dispersive_fee_percent') || fieldName.includes('upfront_service_fee_percentage')) {
            const numericValue = Number(value);
            if (!isNaN(numericValue)) {
                return `${numericValue.toFixed(2)}%`;
            }
        }

        // Handle CVM fields with toFixed(2) precision
        if (fieldName.includes('target_amount')) {
            const numericValue = Number(value);
            if (!isNaN(numericValue)) {
                return numericValue.toFixed(2);
            }
        }

        // Handle CVM percentage fields with toFixed(2) and %
        if (fieldName.includes('on_target_achievement_percentage')) {
            const numericValue = Number(value);
            if (!isNaN(numericValue)) {
                return `${numericValue.toFixed(2)}%`;
            }
        }

        // Handle date fields for identification details (format from YYYY-MM-DD to DD-MM-YYYY)
        if (fieldName.includes('passport_valid_from') || fieldName.includes('passport_valid_to') || 
            fieldName.includes('national_id_valid_from') || fieldName.includes('national_id_valid_to') ||
            fieldName.includes('business_valid_from') || fieldName.includes('business_valid_to')) {
            if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
                const [year, month, day] = value.split('-');
                return `${day}-${month}-${year}`;
            }
        }

        return value;
    };

    /**
     * This function fetches details of a master distributor from an API and updates the state accordingly,
     * handling errors and navigation based on the response.
     */
    const fetchLogDetails = async () => {
        setLoading(true);
        try {
            const logType = searchParams.get('log_type');
            let endpoint = `logs/${id}`;
            if (logType) {
                endpoint = `logs/${id}?log_type=${logType}`;
            } else if (isFundTransferLog) {
                endpoint = `logs/${id}?log_type=Fund Transfer`;
            }
            const response = await getAPI(endpoint);
            if (!response?.error) {
                const temp = response?.data?.data;
                setMasterDistributor(temp);
            } else {
                navigate("/not-found", {
                    state: { route: "/logs" },
                });
            }
        } catch (error) {
            console.error("Error fetching log details:", error);
        }
        setLoading(false);
    };

    useLayoutEffect(() => {
        fetchLogDetails();
    }, []);
    console.log(previousUrl, 'previousUrl')
    return (
        <div dir={notEnglish ? "rtl" : "ltr"}>
            <Breadcrumbs
                heading="View Logs"
                navPath={["Logs", isFundTransferLog ? "Fund Transfer Logs" : isReversalLog ? "Reverse Fund Logs" : "User Activity Logs", "View Logs"]}
                links={previousUrl ? [previousUrl, previousUrl] : [isFundTransferLog ? `/logs/fund-transfer-logs` : isReversalLog ? `/logs/reverse-fund-logs` : `/logs/user-activity-logs`, previousUrl || (isFundTransferLog ? `/logs/fund-transfer-logs` : isReversalLog ? `/logs/reverse-fund-logs` : `/logs/user-activity-logs`), '']}
                noNavigation={false}
            />

            {!loading ? (
                <div className="mt-1.5 bg-white h-maintableheight rounded-lg flex flex-col">
                    <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-[#E5E9EB] flex justify-between items-center">
                        <div className="flex flex-row flex-wrap gap-1 text-[18px] sm:text-[22px]">
                            <span>Log ID - </span>
                            <div data-testid="log-id" className="flex flex-wrap items-start gap-4 text-heading2">
                                {masterDistributorDetails?.log_id}
                            </div>
                        </div>

                    </div>
                    <div
                        className={`flex-grow px-4 sm:px-6 pb-6 overflow-auto scrollBar`}
                    >
                        {isFundTransferLog ? (
                            <>
                                {(masterDistributorDetails as any)?.action_taken.includes('Recharge') ? (
                                    <>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8">
                                            <div className="mt-6">
                                                <h1 className="text-sm text-gray-600">Transaction ID</h1>
                                                <p className="mt-1 font-medium text-gray-900" data-testid="transaction-id">{(masterDistributorDetails as any)?.transaction_id || '-'}</p>
                                            </div>
                                            <div className="mt-6">
                                                <h1 className="text-sm text-gray-600">Transaction Date and Time</h1>
                                                <p className="mt-1 font-medium text-gray-900" data-testid="transaction-date-and-time">{(masterDistributorDetails as any)?.transaction_date ? moment((masterDistributorDetails as any)?.transaction_date).format('DD-MM-YYYY, hh:mm A') : '-'}</p>
                                            </div>
                                            <div className="mt-6">
                                                <h1 className="text-sm text-gray-600">Transaction Type</h1>
                                                <p className="mt-1 font-medium text-gray-900" data-testid="transaction-type">{(masterDistributorDetails as any)?.action_taken || '-'}</p>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8">
                                            <div className="mt-6">
                                                <h1 className="text-sm text-gray-600">MNO Name</h1>
                                                <p className="mt-1 font-medium text-gray-900" data-testid="mno-name">{(masterDistributorDetails as any)?.mno_name || '-'}</p>
                                            </div>
                                            <div className="mt-6">
                                                <h1 className="text-sm text-gray-600">Phone Number</h1>
                                                <p className="mt-1 font-medium text-gray-900" data-testid="phone-number">{(masterDistributorDetails as any)?.phone_number || '-'}</p>
                                            </div>
                                            <div className="mt-6">
                                                <h1 className="text-sm text-gray-600">Sender</h1>
                                                <p className="mt-1 font-medium text-gray-900" data-testid="admin-user-location">{(masterDistributorDetails as any)?.receiver || '-'}</p>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8">
                                            <div className="mt-6">
                                                <h1 className="text-sm text-gray-600">Sender Location</h1>
                                                <p className="mt-1 font-medium text-gray-900" data-testid="recipient-user-location">{(masterDistributorDetails as any)?.receiver_location || '-'}</p>
                                            </div>
                                            <div className="mt-6">
                                                <h1 className="text-sm text-gray-600">Transaction Status</h1>
                                                <p className="mt-1 font-medium text-gray-900" data-testid="transaction-status">{(masterDistributorDetails as any)?.transaction_status || '-'}</p>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8">
                                            <div className="mt-6">
                                                <h1 className="text-sm text-gray-600">Transaction ID</h1>
                                                <p className="mt-1 font-medium text-gray-900" data-testid="transaction-id">{(masterDistributorDetails as any)?.transaction_id || '-'}</p>
                                            </div>
                                            <div className="mt-6">
                                                <h1 className="text-sm text-gray-600">Transaction Date and Time</h1>
                                                <p className="mt-1 font-medium text-gray-900" data-testid="transaction-date-and-time">{(masterDistributorDetails as any)?.transaction_date ? moment((masterDistributorDetails as any)?.transaction_date).format('DD-MM-YYYY, hh:mm A') : '-'}</p>
                                            </div>
                                            <div className="mt-6">
                                                <h1 className="text-sm text-gray-600">Transaction Type</h1>
                                                <p className="mt-1 font-medium text-gray-900" data-testid="transaction-type">{(masterDistributorDetails as any)?.action_taken || '-'}</p>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8">
                                            <div className="mt-6">
                                                <h1 className="text-sm text-gray-600">Requested Amount</h1>
                                                <p className="mt-1 font-medium text-gray-900" data-testid="requested-amount">{(masterDistributorDetails as any)?.amount ? `AFA ${(masterDistributorDetails as any)?.amount}` : '-'}</p>
                                            </div>
                                            <div className="mt-6">
                                        <h1 className="text-sm text-gray-600">Sender</h1>
                                                <p className="mt-1 font-medium text-gray-900" data-testid="admin-user-code">{(masterDistributorDetails as any)?.sender || '-'}</p>
                                            </div>
                                            <div className="mt-6">
                                                <h1 className="text-sm text-gray-600">Receiver</h1>
                                                <p className="mt-1 font-medium text-gray-900" data-testid="recipient-user-code">{(masterDistributorDetails as any)?.receiver || '-'}</p>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8">
                                            <div className="mt-6">
                                                <h1 className="text-sm text-gray-600">Wallet Type</h1>
                                                <p className="mt-1 font-medium text-gray-900" data-testid="wallet-type">{(masterDistributorDetails as any)?.wallet_type?.replace('etopup', 'eTop Up').replace('Etopup', 'eTop Up') || '-'}</p>
                                            </div>
                                            <div className="mt-6">
                                                <h1 className="text-sm text-gray-600">Source of Payment</h1>
                                                <p className="mt-1 font-medium text-gray-900" data-testid="source-of-payment">{(masterDistributorDetails as any)?.source_of_payment || '-'}</p>
                                            </div>
                                            <div className="mt-6">
                                                <h1 className="text-sm text-gray-600">Payment Type</h1>
                                                <p className="mt-1 font-medium text-gray-900" data-testid="payment-type">{(masterDistributorDetails as any)?.payment_type ? (masterDistributorDetails as any)?.payment_type.charAt(0).toUpperCase() + (masterDistributorDetails as any)?.payment_type.slice(1) : '-'}</p>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8">
                                            <div className="mt-6">
                                                <h1 className="text-sm text-gray-600">Transaction Status</h1>
                                                <p className="mt-1 font-medium text-gray-900" data-testid="transaction-status">{(masterDistributorDetails as any)?.transaction_status || '-'}</p>
                                            </div>
                                            <div className="mt-6">
                                                <h1 className="text-sm text-gray-600">Sender Location</h1>
                                                <p className="mt-1 font-medium text-gray-900" data-testid="sender-location">{(masterDistributorDetails as any)?.sender_location || '-'}</p>
                                            </div>
                                            <div className="mt-6">
                                                <h1 className="text-sm text-gray-600">Receiver Location</h1>
                                                <p className="mt-1 font-medium text-gray-900" data-testid="receiver-location">{(masterDistributorDetails as any)?.receiver_location || '-'}</p>
                                            </div>
                                        </div>
                                        <div className="mt-6">
                                            <h1 className="text-sm text-gray-600">Payment Receipt</h1>
                                            {(masterDistributorDetails as any)?.payment_receipt ? (
                                                <div className="max-w-fit flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1">
                                                    <span className="more-lines">{(masterDistributorDetails as any)?.payment_receipt?.split("/").pop() || 'Filename.png'}</span>
                                                    <ToolTip ToolTipItem={(masterDistributorDetails as any)?.payment_receipt?.split("/").pop() || 'Filename.png'} top="top-0" />
                                                    <Image src="file-eye" className="cursor-pointer" objectFit={'contain'} onClick={() => {
                                                        const fileUrl = config?.cdnURL + 'public/' + (masterDistributorDetails as any)?.payment_receipt;
                                                        window.open(fileUrl, "_blank");
                                                    }} />
                                                </div>
                                            ) : (
                                                <p className="mt-1 font-medium text-gray-900">-</p>
                                            )}
                                        </div>
                                        <div className="mt-6">
                                            <h1 className="text-sm text-gray-600">Note</h1>
                                            <p className="mt-1 font-medium text-gray-900" data-testid="note">{(masterDistributorDetails as any)?.note || '-'}</p>
                                        </div>
                                    </>
                                )}
                            </>
                        ) : isReversalLog ? (
                            <>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8">
                                    <div className="mt-6">
                                        <h1 className="text-sm text-gray-600">Transaction ID</h1>
                                        <p className="mt-1 font-medium text-gray-900" data-testid="transaction-id">{(masterDistributorDetails as any)?.transaction_id || '-'}</p>
                                    </div>
                                    <div className="mt-6">
                                        <h1 className="text-sm text-gray-600">Transaction Date and Time</h1>
                                        <p className="mt-1 font-medium text-gray-900" data-testid="transaction-date-and-time">{(masterDistributorDetails as any)?.transaction_date ? moment((masterDistributorDetails as any)?.transaction_date).format('DD-MM-YYYY, hh:mm A') : '-'}</p>
                                    </div>
                                    <div className="mt-6">
                                        <h1 className="text-sm text-gray-600">Type of Transaction</h1>
                                        <p className="mt-1 font-medium text-gray-900" data-testid="type-of-transaction">{(masterDistributorDetails as any)?.transaction_type || '-'}</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8">
                                    <div className="mt-6">
                                        <h1 className="text-sm text-gray-600">Action Taken By</h1>
                                        <p className="mt-1 font-medium text-gray-900" data-testid="action-taken-by">{(masterDistributorDetails as any)?.action_taken_by || '-'}</p>
                                    </div>
                                    <div className="mt-6">
                                        <h1 className="text-sm text-gray-600">Sender</h1>
                                        <p className="mt-1 font-medium text-gray-900" data-testid="sender">{(masterDistributorDetails as any)?.sender || '-'}</p>
                                    </div>
                                    <div className="mt-6">
                                        <h1 className="text-sm text-gray-600">{(masterDistributorDetails as any)?.transaction_type === 'Recharge' ? 'MNO Name' : 'Receiver'}</h1>
                                        <p className="mt-1 font-medium text-gray-900" data-testid="receiver">{(masterDistributorDetails as any)?.transaction_type === 'Recharge' ? ((masterDistributorDetails as any)?.mno_name || '-') : ((masterDistributorDetails as any)?.receiver || '-')}</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8">
                                    <div className="mt-6">
                                        <h1 className="text-sm text-gray-600">Amount</h1>
                                        <p className="mt-1 font-medium text-gray-900" data-testid="amount">{(masterDistributorDetails as any)?.amount || '-'}</p>
                                    </div>
                                    <div className="mt-6">
                                        <h1 className="text-sm text-gray-600">Action Taken</h1>
                                        <p className="mt-1 font-medium text-gray-900" data-testid="action-taken">{(masterDistributorDetails as any)?.action_taken || '-'}</p>
                                    </div>
                                    <div className="mt-6">
                                        <h1 className="text-sm text-gray-600">Reversal Raised Date and Time</h1>
                                        <p className="mt-1 font-medium text-gray-900" data-testid="reversal-raised-date-and-time">{(masterDistributorDetails as any)?.flagged_at ? moment((masterDistributorDetails as any)?.flagged_at).format('DD-MM-YYYY, hh:mm A') : '-'}</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8">
                                    <div className="mt-6">
                                        <h1 className="text-sm text-gray-600">Reversal Resolved Date and Time</h1>
                                        <p className="mt-1 font-medium text-gray-900" data-testid="reversal-resolved-date-and-time">{(masterDistributorDetails as any)?.date ? moment((masterDistributorDetails as any)?.date).format('DD-MM-YYYY, hh:mm A') : '-'}</p>
                                    </div>
                                </div>
                                <div className="mt-6">
                                    <h1 className="text-sm text-gray-600">Reason for Rejection</h1>
                                    <p className="mt-1 font-medium text-gray-900" data-testid="reason-for-rejection">{(masterDistributorDetails as any)?.rejection_reason ? (masterDistributorDetails as any)?.rejection_reason : '-'}</p>
                                </div>

                            </>
                        ) : (
                            <>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8">
                                    <div className="mt-6">
                                        <h1 className="text-sm text-gray-600">Action Taken On</h1>
                                        <p className="mt-1 font-medium text-gray-900" data-testid="action-taken-on">{masterDistributorDetails?.action_taken_on}</p>
                                    </div>
                                    <div className="mt-6">
                                        <h1 className="text-sm text-gray-600">Initiated By</h1>
                                        <p className="mt-1 font-medium text-gray-900" data-testid="initiated-by">{masterDistributorDetails?.initiated_by}</p>
                                    </div>
                                    <div className="mt-6">
                                        <h1 className="text-sm text-gray-600">Action Taken</h1>
                                        <p className="mt-1 font-medium text-gray-900" data-testid="action-taken">{masterDistributorDetails?.action_taken}</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8">
                                    <div className="mt-6">
                                        <h1 className="text-sm text-gray-600">Date and Time</h1>
                                        <p className="mt-1 font-medium text-gray-900" data-testid="date-and-time">{moment(masterDistributorDetails?.date).format('DD-MM-YYYY, hh:mm A')}</p>
                                    </div>
                                    <div className="mt-6">
                                        <h1 className="text-sm text-gray-600">Initiated User Location</h1>
                                        <p className="mt-1 font-medium text-gray-900" data-testid="initiated-user-location">{masterDistributorDetails?.initiated_by_location}</p>
                                    </div>
                                    <div className="mt-6">
                                        <h1 className="text-sm text-gray-600">Action Taken On User Location</h1>
                                        <p className="mt-1 font-medium text-gray-900" data-testid="action-taken-on-user-location">{masterDistributorDetails?.action_taken_on_location}</p>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Before and After Values Section */}
                        {masterDistributorDetails?.action_taken === 'Edit' && masterDistributorDetails?.logs?.sections && (
                            <div className="mt-8 bg-background_blue rounded-lg p-4 sm:p-6">
                                {/* Before Values */}
                                <div className="mb-8">
                                    <h2 className="text-lg font-semibold text-gray-900 mb-4 underline">Before Values</h2>
                                    <div className={`flex gap-6 ${Object.entries(masterDistributorDetails.logs.sections).some(([, section]) =>
                                        section.changed && section.changes.some(change => change.field === 'service_fee_details')
                                    ) ? 'flex-col' : 'flex-row flex-wrap'}`}>
                                        {Object.entries(masterDistributorDetails.logs.sections)
                                            .sort(([a], [b]) => {
                                                // Sort business_assigned_area to the end
                                                if (a === 'business_assigned_area') return 1;
                                                if (b === 'business_assigned_area') return -1;
                                                return 0;
                                            })
                                            .map(([sectionKey, section]) => {
                                                if (!section.changed) return null;

                                                // Handle bank_details specifically
                                                if (sectionKey === 'bank_details') {
                                                    // Check if this is individual field changes (like bank_details[0].bank_name)
                                                    const hasArrayIndices = section.changes.some(change => /\[\d+\]/.test(change.field));

                                                    if (hasArrayIndices) {
                                                        // Group changes by array index
                                                        const groupedChanges: { [key: string]: LogChange[] } = {};

                                                        section.changes.forEach(change => {
                                                            const match = change.field.match(/\[(\d+)\]/);
                                                            if (match) {
                                                                const index = match[1];
                                                                const key = `bank_${index}`;
                                                                if (!groupedChanges[key]) {
                                                                    groupedChanges[key] = [];
                                                                }
                                                                groupedChanges[key].push({
                                                                    ...change,
                                                                    field: change.field.replace(/\[\d+\]\./, '').replace(/^service_fee\./, '').replace(/^service_fee_details\./, '') // Remove index and service_fee/service_fee_details prefixes
                                                                });
                                                            }
                                                        });

                                                        return Object.entries(groupedChanges).map(([groupKey, groupChanges]) => {
                                                            const itemIndex = groupKey.replace('bank_', '');
                                                            return (
                                                                <div key={`${sectionKey}-${groupKey}-before`} className="w-full mb-4">
                                                                    <h3 className="text-base font-semibold text-gray-800 mb-3">Bank Details ({Number(itemIndex) + 1})</h3>
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                        {groupChanges.map((change, index) => (
                                                                            <div key={`${groupKey}-${index}-before`} className="w-full">
                                                                                <h4 className="text-sm text-gray-600">{cleanFieldName(change.field)}</h4>
                                                                                <div className="mt-1 font-medium text-gray-900" data-testid={`before-${cleanFieldName(change.field).toLowerCase().replace(/\s+/g, '-')}`}>
                                                                                    {formatValue(change.before_value, change.field)}
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            );
                                                        });
                                                    } else {
                                                        // Handle entire array changes (previous structure)
                                                        return section.changes.map((change, index) => {
                                                            // Check if before_value is empty array, null, or undefined
                                                            const isEmpty = !change.before_value || 
                                                                (Array.isArray(change.before_value) && change.before_value.length === 0) ||
                                                                change.before_value === null ||
                                                                change.before_value === undefined;
                                                            
                                                            return (
                                                                <div key={`${sectionKey}-${index}-before`} className="w-full mb-4">
                                                                    <h3 className="text-base font-semibold text-gray-800 mb-3">Bank Details</h3>
                                                                    <div className="w-full">
                                                                        {isEmpty ? '-' : formatValue(change.before_value, 'bank_details')}
                                                                    </div>
                                                                </div>
                                                            );
                                                        });
                                                    }
                                                }

                                                // Handle service_fee individual field changes
                                                if (sectionKey === 'service_fee') {
                                                    // Check if this is individual field changes (like service_fee[0].mno_id)
                                                    const hasArrayIndices = section.changes.some(change => /\[\d+\]/.test(change.field));

                                                    if (hasArrayIndices) {
                                                        // Group changes by array index
                                                        const groupedChanges: { [key: string]: LogChange[] } = {};

                                                        section.changes.forEach(change => {
                                                            const match = change.field.match(/\[(\d+)\]/);
                                                            if (match) {
                                                                const index = match[1];
                                                                const key = `service_${index}`;
                                                                if (!groupedChanges[key]) {
                                                                    groupedChanges[key] = [];
                                                                }
                                                                groupedChanges[key].push({
                                                                    ...change,
                                                                    field: change.field.replace(/\[\d+\]\./, '').replace(/^service_fee\./, '').replace(/^service_fee_details\./, '') // Remove index and service_fee/service_fee_details prefixes
                                                                });
                                                            }
                                                        });

                                                        return Object.entries(groupedChanges).map(([groupKey, groupChanges]) => {
                                                            const itemIndex = groupKey.replace('service_', '');
                                                            // Check if any change in this group has mno_name
                                                            const mnoName = groupChanges.find(change => 
                                                                (change.after_value && typeof change.after_value === 'object' && change.after_value.mno_name) ||
                                                                (change.before_value && typeof change.before_value === 'object' && change.before_value.mno_name)
                                                            )?.after_value?.mno_name || groupChanges.find(change => 
                                                                change.before_value && typeof change.before_value === 'object' && change.before_value.mno_name
                                                            )?.before_value?.mno_name;
                                                            const title = mnoName ? `Service Fee Details (${mnoName})` : `Service Fee Details (${Number(itemIndex) + 1})`;
                                                            return (
                                                                <div key={`${sectionKey}-${groupKey}-before`} className="w-full mb-4">
                                                                    <h3 className="text-base font-semibold text-gray-800 mb-3">{title}</h3>
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                        {groupChanges.map((change, index) => (
                                                                            <div key={`${groupKey}-${index}-before`} className="w-full">
                                                                                <h4 className="text-sm text-gray-600">{cleanFieldName(change.field)}</h4>
                                                                                <div className="mt-1 font-medium text-gray-900" data-testid={`before-${cleanFieldName(change.field).toLowerCase().replace(/\s+/g, '-')}`}>
                                                                                    {formatValue(change.before_value, change.field)}
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            );
                                                        });
                                                    } else {
                                                        // Handle entire array changes (previous structure)
                                                        return section.changes.map((change, index) => {
                                                            // Check if this is service_fee_details array
                                                            const isServiceFeeDetails = change.field.includes('service_fee_details');
                                                            
                                                            if (isServiceFeeDetails) {
                                                                return (
                                                                    <div key={`${sectionKey}-${index}-before`} className="w-full mb-4">
                                                                        <h3 className="text-base font-semibold text-gray-800 mb-3">Service Fee Details</h3>
                                                                        <div className="w-full">
                                                                            {formatValue(change.before_value, 'service_fee_details')}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            } else {
                                                                // Handle main service fee fields (service_fee_type, service_fee_parent, upfront_service_fee_percentage)
                                                                return (
                                                                    <div key={`${sectionKey}-${index}-before`} className="w-full sm:w-[30%]">
                                                                        <h3 className="text-sm text-gray-600 capitalize">{cleanFieldName(change.field).replace(/_/g, ' ').replace(/\./g, ' ')}</h3>
                                                                        <div className="mt-1 font-medium text-gray-900" data-testid={`before-${cleanFieldName(change.field).toLowerCase().replace(/\s+/g, '-')}`}>
                                                                            {formatValue(change.before_value, change.field)}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            }
                                                        });
                                                    }
                                                }


                                            // Handle identification_details individual field changes
                                            if (sectionKey === 'identification_details') {
                                                // Check if there's a change in identification_type
                                                const hasIdentificationTypeChange = section.changes.some(change => 
                                                    change.field.includes('identification_type')
                                                );
                                                
                                                if (hasIdentificationTypeChange) {
                                                    // OLD LOGIC: Filter changes that have before_value (original behavior)
                                                    const beforeChanges = section.changes.filter(change => change.before_value !== undefined);
                                                    
                                                    if (beforeChanges.length > 0) {
                                                        return (
                                                            <div key={`${sectionKey}-before`} className="w-full mb-4">
                                                                <h3 className="text-base font-semibold text-gray-800 mb-3">Identification Details</h3>
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                    {beforeChanges.map((change, index) => {
                                                                        // Clean field name by removing identification_details. prefix
                                                                        const cleanField = change.field.replace(/^identification_details\./, '').replace(/^document_details\./, '');
                                                                        // Show "-" for mother's name if identification_type change, otherwise show actual value
                                                                        const displayValue = formatValue(change.before_value, cleanField);
                                                                        return (
                                                                            <div key={`${sectionKey}-${index}-before`} className="w-full">
                                                                                <h4 className="text-sm text-gray-600">{cleanFieldName(cleanField)}</h4>
                                                                                <div className="mt-1 font-medium text-gray-900" data-testid={`before-${cleanFieldName(cleanField).toLowerCase().replace(/\s+/g, '-')}`}>
                                                                                    {displayValue}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                } else {
                                                    // NEW LOGIC: Get all changes that have after_value (to include mother's name even if no before_value)
                                                    const allChanges = section.changes.filter(change => change.after_value !== undefined);
                                                    
                                                    if (allChanges.length > 0) {
                                                        return (
                                                            <div key={`${sectionKey}-before`} className="w-full mb-4">
                                                                <h3 className="text-base font-semibold text-gray-800 mb-3">Identification Details</h3>
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                    {allChanges.map((change, index) => {
                                                                        // Clean field name by removing identification_details. prefix
                                                                        const cleanField = change.field.replace(/^identification_details\./, '').replace(/^document_details\./, '');
                                                                        // Show actual value for mother's name (no special logic needed)
                                                                        const displayValue = formatValue(change.before_value, cleanField);
                                                                        return (
                                                                            <div key={`${sectionKey}-${index}-before`} className="w-full">
                                                                                <h4 className="text-sm text-gray-600">{cleanFieldName(cleanField)}</h4>
                                                                                <div className="mt-1 font-medium text-gray-900" data-testid={`before-${cleanFieldName(cleanField).toLowerCase().replace(/\s+/g, '-')}`}>
                                                                                    {displayValue}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                }
                                                return null;
                                            }
                                            
                                                // Handle business_details individual field changes
                                                if (sectionKey === 'business_details') {
                                                    // Filter changes that have before_value
                                                    const beforeChanges = section.changes.filter(change => change.before_value !== undefined);
                                                    
                                                    if (beforeChanges.length > 0) {
                                                        return (
                                                            <div key={`${sectionKey}-before`} className="w-full mb-4">
                                                                <h3 className="text-base font-semibold text-gray-800 mb-3">Business Details</h3>
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                    {beforeChanges.map((change, index) => {
                                                                        // Clean field name by removing business_details. prefix
                                                                        const cleanField = change.field.replace(/^business_details\./, '');
                                                                        return (
                                                                            <div key={`${sectionKey}-${index}-before`} className="w-full">
                                                                                <h4 className="text-sm text-gray-600">{cleanFieldName(cleanField)}</h4>
                                                                                <div className="mt-1 font-medium text-gray-900" data-testid={`before-${cleanFieldName(cleanField).toLowerCase().replace(/\s+/g, '-')}`}>
                                                                                    {formatValue(change.before_value, cleanField)}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                }

                                                // Handle CVM individual field changes
                                                if (sectionKey === 'cvm') {
                                                    // Check if this is individual field changes (like cvm[0].target_amount)
                                                    const hasArrayIndices = section.changes.some(change => /\[\d+\]/.test(change.field));

                                                    if (hasArrayIndices) {
                                                        // Group changes by array index
                                                        const groupedChanges: { [key: string]: LogChange[] } = {};

                                                        section.changes.forEach(change => {
                                                            const match = change.field.match(/\[(\d+)\]/);
                                                            if (match) {
                                                                const index = match[1];
                                                                const key = `cvm_${index}`;
                                                                if (!groupedChanges[key]) {
                                                                    groupedChanges[key] = [];
                                                                }
                                                                groupedChanges[key].push({
                                                                    ...change,
                                                                    field: change.field.replace(/\[\d+\]\./, '').replace(/^cvm\./, '').replace(/^targets\./, '') // Remove index and cvm/targets prefixes
                                                                });
                                                            }
                                                        });

                                                        return (
                                                            <div key={`${sectionKey}-before`} className="w-full mb-4">
                                                                <h3 className="text-base font-semibold text-gray-800 mb-3">CVM</h3>
                                                                {Object.entries(groupedChanges).map(([groupKey, groupChanges]) => {
                                                                    const itemIndex = groupKey.replace('cvm_', '');
                                                                    return (
                                                                        <div key={`${sectionKey}-${groupKey}-before`} className="w-full mb-4">
                                                                            <h4 className="text-sm font-semibold text-gray-700 mb-2">CVM Target ({Number(itemIndex) + 1})</h4>
                                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                                {groupChanges.map((change, index) => (
                                                                                    <div key={`${groupKey}-${index}-before`} className="w-full">
                                                                                        <h5 className="text-sm text-gray-600">{cleanFieldName(change.field)}</h5>
                                                                                        <div className="mt-1 font-medium text-gray-900" data-testid={`before-${cleanFieldName(change.field).toLowerCase().replace(/\s+/g, '-')}`}>
                                                                                            {formatValue(change.before_value, change.field)}
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        );
                                                    } else {
                                                        // Handle entire array changes (previous structure)
                                                        return (
                                                            <div key={`${sectionKey}-before`} className="w-full mb-4">
                                                                <h3 className="text-base font-semibold text-gray-800 mb-3">CVM</h3>
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                    {section.changes.map((change, index) => {
                                                                        // Check if this is targets array
                                                                        const isTargetsArray = change.field.includes('targets');
                                                                        
                                                                        if (isTargetsArray) {
                                                                            return (
                                                                                <div key={`${sectionKey}-${index}-before`} className="w-full">
                                                                                    <h4 className="text-sm font-semibold text-gray-700 mb-2">CVM Targets</h4>
                                                                                    <div className="w-full">
                                                                                        {formatValue(change.before_value, 'targets')}
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        } else {
                                                                            // Handle main CVM fields (from_date, to_date)
                                                                            return (
                                                                                <div key={`${sectionKey}-${index}-before`} className="w-full">
                                                                                    <h4 className="text-sm text-gray-600">{cleanFieldName(change.field)}</h4>
                                                                                    <div className="mt-1 font-medium text-gray-900" data-testid={`before-${cleanFieldName(change.field).toLowerCase().replace(/\s+/g, '-')}`}>
                                                                                        {formatValue(change.before_value, change.field)}
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        }
                                                                    })}
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                }

                                                // Regular rendering for other sections
                                                return section.changes.map((change, index) => (
                                                    <div key={`${sectionKey}-${index}-before`} className={`${change.field === 'business_assigned_area' ? 'w-full' : 'w-full sm:w-[30%]'}`}>
                                                        <h3 className="text-sm text-gray-600 capitalize">{cleanFieldName(change.field).replace(/_/g, ' ').replace(/\./g, ' ')}</h3>
                                                        <div className="mt-1 font-medium text-gray-900" data-testid={`before-${cleanFieldName(change.field).toLowerCase().replace(/\s+/g, '-')}`}>
                                                            {formatValue(change.before_value, change.field)}
                                                        </div>
                                                    </div>
                                                )
                                                )
                                            })
                                        }
                                    </div>
                                </div>

                                {/* After Values */}
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900 mb-4 underline">After Values</h2>
                                    <div className={`flex gap-6 ${Object.entries(masterDistributorDetails.logs.sections).some(([, section]) =>
                                        section.changed && section.changes.some(change => change.field === 'service_fee_details')
                                    ) ? 'flex-col' : 'flex-row flex-wrap'}`}>
                                        {Object.entries(masterDistributorDetails.logs.sections)
                                            .sort(([a], [b]) => {
                                                // Sort business_assigned_area to the end
                                                if (a === 'business_assigned_area') return 1;
                                                if (b === 'business_assigned_area') return -1;
                                                return 0;
                                            })
                                            .map(([sectionKey, section]) => {
                                                if (!section.changed) return null;

                                                // Handle bank_details specifically
                                                if (sectionKey === 'bank_details') {
                                                    // Check if this is individual field changes (like bank_details[0].bank_name)
                                                    const hasArrayIndices = section.changes.some(change => /\[\d+\]/.test(change.field));

                                                    if (hasArrayIndices) {
                                                        // Group changes by array index
                                                        const groupedChanges: { [key: string]: LogChange[] } = {};

                                                        section.changes.forEach(change => {
                                                            const match = change.field.match(/\[(\d+)\]/);
                                                            if (match) {
                                                                const index = match[1];
                                                                const key = `bank_${index}`;
                                                                if (!groupedChanges[key]) {
                                                                    groupedChanges[key] = [];
                                                                }
                                                                groupedChanges[key].push({
                                                                    ...change,
                                                                    field: change.field.replace(/\[\d+\]\./, '').replace(/^service_fee\./, '').replace(/^service_fee_details\./, '') // Remove index and service_fee/service_fee_details prefixes
                                                                });
                                                            }
                                                        });

                                                        return Object.entries(groupedChanges).map(([groupKey, groupChanges]) => {
                                                            const itemIndex = groupKey.replace('bank_', '');
                                                            return (
                                                                <div key={`${sectionKey}-${groupKey}-after`} className="w-full mb-4">
                                                                    <h3 className="text-base font-semibold text-gray-800 mb-3">Bank Details ({Number(itemIndex) + 1})</h3>
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                        {groupChanges.map((change, index) => (
                                                                            <div key={`${groupKey}-${index}-after`} className="w-full">
                                                                                <h4 className="text-sm text-gray-600">{cleanFieldName(change.field)}</h4>
                                                                                <div className="mt-1 font-medium text-gray-900" data-testid={`after-${cleanFieldName(change.field).toLowerCase().replace(/\s+/g, '-')}`}>
                                                                                    {formatValue(change.after_value, change.field)}
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            );
                                                        });
                                                    } else {
                                                        // Handle entire array changes (previous structure)
                                                        return section.changes.map((change, index) => {
                                                            // Check if after_value is empty array, null, or undefined
                                                            const isEmpty = !change.after_value || 
                                                                (Array.isArray(change.after_value) && change.after_value.length === 0) ||
                                                                change.after_value === null ||
                                                                change.after_value === undefined;
                                                            
                                                            return (
                                                                <div key={`${sectionKey}-${index}-after`} className="w-full mb-4">
                                                                    <h3 className="text-base font-semibold text-gray-800 mb-3">Bank Details</h3>
                                                                    <div className="w-full">
                                                                        {isEmpty ? '-' : formatValue(change.after_value, 'bank_details')}
                                                                    </div>
                                                                </div>
                                                            );
                                                        });
                                                    }
                                                }

                                                // Handle service_fee individual field changes
                                                if (sectionKey === 'service_fee') {
                                                    // Check if this is individual field changes (like service_fee[0].mno_id)
                                                    const hasArrayIndices = section.changes.some(change => /\[\d+\]/.test(change.field));

                                                    if (hasArrayIndices) {
                                                        // Group changes by array index
                                                        const groupedChanges: { [key: string]: LogChange[] } = {};

                                                        section.changes.forEach(change => {
                                                            const match = change.field.match(/\[(\d+)\]/);
                                                            if (match) {
                                                                const index = match[1];
                                                                const key = `service_${index}`;
                                                                if (!groupedChanges[key]) {
                                                                    groupedChanges[key] = [];
                                                                }
                                                                groupedChanges[key].push({
                                                                    ...change,
                                                                    field: change.field.replace(/\[\d+\]\./, '').replace(/^service_fee\./, '').replace(/^service_fee_details\./, '') // Remove index and service_fee/service_fee_details prefixes
                                                                });
                                                            }
                                                        });

                                                        return Object.entries(groupedChanges).map(([groupKey, groupChanges]) => {
                                                            const itemIndex = groupKey.replace('service_', '');
                                                            // Check if any change in this group has mno_name
                                                            const mnoName = groupChanges.find(change => 
                                                                (change.after_value && typeof change.after_value === 'object' && change.after_value.mno_name) ||
                                                                (change.before_value && typeof change.before_value === 'object' && change.before_value.mno_name)
                                                            )?.after_value?.mno_name || groupChanges.find(change => 
                                                                change.before_value && typeof change.before_value === 'object' && change.before_value.mno_name
                                                            )?.before_value?.mno_name;
                                                            const title = mnoName ? `Service Fee Details (${mnoName})` : `Service Fee Details (${Number(itemIndex) + 1})`;
                                                            return (
                                                                <div key={`${sectionKey}-${groupKey}-after`} className="w-full mb-4">
                                                                    <h3 className="text-base font-semibold text-gray-800 mb-3">{title}</h3>
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                        {groupChanges.map((change, index) => (
                                                                            <div key={`${groupKey}-${index}-after`} className="w-full">
                                                                                <h4 className="text-sm text-gray-600">{cleanFieldName(change.field)}</h4>
                                                                                <div className="mt-1 font-medium text-gray-900" data-testid={`after-${cleanFieldName(change.field).toLowerCase().replace(/\s+/g, '-')}`}>
                                                                                    {formatValue(change.after_value, change.field)}
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            );
                                                        });
                                                    } else {
                                                        // Handle entire array changes (previous structure)
                                                        return section.changes.map((change, index) => {
                                                            // Check if this is service_fee_details array
                                                            const isServiceFeeDetails = change.field.includes('service_fee_details');
                                                            
                                                            if (isServiceFeeDetails) {
                                                                return (
                                                                    <div key={`${sectionKey}-${index}-after`} className="w-full mb-4">
                                                                        <h3 className="text-base font-semibold text-gray-800 mb-3">Service Fee Details</h3>
                                                                        <div className="w-full">
                                                                            {formatValue(change.after_value, 'service_fee_details')}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            } else {
                                                                // Handle main service fee fields (service_fee_type, service_fee_parent, upfront_service_fee_percentage)
                                                                return (
                                                                    <div key={`${sectionKey}-${index}-after`} className="w-full sm:w-[30%]">
                                                                        <h3 className="text-sm text-gray-600 capitalize">{cleanFieldName(change.field).replace(/_/g, ' ').replace(/\./g, ' ')}</h3>
                                                                        <div className="mt-1 font-medium text-gray-900" data-testid={`after-${cleanFieldName(change.field).toLowerCase().replace(/\s+/g, '-')}`}>
                                                                            {formatValue(change.after_value, change.field)}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            }
                                                        });
                                                    }
                                                }


                                                // Handle identification_details individual field changes
                                                if (sectionKey === 'identification_details') {
                                                    // Check if there's a change in identification_type
                                                    const hasIdentificationTypeChange = section.changes.some(change => 
                                                        change.field.includes('identification_type')
                                                    );
                                                    
                                                    if (hasIdentificationTypeChange) {
                                                        // OLD LOGIC: Filter changes that have after_value (original behavior)
                                                        const afterChanges = section.changes.filter(change => change.after_value !== undefined);
                                                        
                                                        if (afterChanges.length > 0) {
                                                            return (
                                                                <div key={`${sectionKey}-after`} className="w-full mb-4">
                                                                    <h3 className="text-base font-semibold text-gray-800 mb-3">Identification Details</h3>
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                        {afterChanges.map((change, index) => {
                                                                            // Clean field name by removing identification_details. prefix
                                                                            const cleanField = change.field.replace(/^identification_details\./, '').replace(/^document_details\./, '');
                                                                            // For mother's name: show "-" if identification_type change, otherwise show actual value
                                                                            const displayValue = (hasIdentificationTypeChange && cleanField.includes('mothers_name')) 
                                                                                ? '-' 
                                                                                : formatValue(change.after_value, cleanField);
                                                                            return (
                                                                                <div key={`${sectionKey}-${index}-after`} className="w-full">
                                                                                    <h4 className="text-sm text-gray-600">{cleanFieldName(cleanField)}</h4>
                                                                                <div className="mt-1 font-medium text-gray-900" data-testid={`after-${cleanFieldName(cleanField).toLowerCase().replace(/\s+/g, '-')}`}>
                                                                                    {displayValue}
                                                                                </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            );
                                                        }
                                                    } else {
                                                        // NEW LOGIC: Filter changes that have after_value (show actual values)
                                                        const afterChanges = section.changes.filter(change => change.after_value !== undefined);
                                                        
                                                        if (afterChanges.length > 0) {
                                                            return (
                                                                <div key={`${sectionKey}-after`} className="w-full mb-4">
                                                                    <h3 className="text-base font-semibold text-gray-800 mb-3">Identification Details</h3>
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                        {afterChanges.map((change, index) => {
                                                                            // Clean field name by removing identification_details. prefix
                                                                            const cleanField = change.field.replace(/^identification_details\./, '').replace(/^document_details\./, '');
                                                                            // Show actual value (no special logic for mother's name)
                                                                            const displayValue = formatValue(change.after_value, cleanField);
                                                                            return (
                                                                                <div key={`${sectionKey}-${index}-after`} className="w-full">
                                                                                    <h4 className="text-sm text-gray-600">{cleanFieldName(cleanField)}</h4>
                                                                                <div className="mt-1 font-medium text-gray-900" data-testid={`after-${cleanFieldName(cleanField).toLowerCase().replace(/\s+/g, '-')}`}>
                                                                                    {displayValue}
                                                                                </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            );
                                                        }
                                                    }
                                                    return null;
                                                }
                                                
                                                // Handle business_details individual field changes
                                                if (sectionKey === 'business_details') {
                                                    // Filter changes that have after_value
                                                    const afterChanges = section.changes.filter(change => change.after_value !== undefined);
                                                    
                                                    if (afterChanges.length > 0) {
                                                        return (
                                                            <div key={`${sectionKey}-after`} className="w-full mb-4">
                                                                <h3 className="text-base font-semibold text-gray-800 mb-3">Business Details</h3>
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                    {afterChanges.map((change, index) => {
                                                                        // Clean field name by removing business_details. prefix
                                                                        const cleanField = change.field.replace(/^business_details\./, '');
                                                                        return (
                                                                            <div key={`${sectionKey}-${index}-after`} className="w-full">
                                                                                <h4 className="text-sm text-gray-600">{cleanFieldName(cleanField)}</h4>
                                                                                <div className="mt-1 font-medium text-gray-900" data-testid={`after-${cleanFieldName(cleanField).toLowerCase().replace(/\s+/g, '-')}`}>
                                                                                    {formatValue(change.after_value, cleanField)}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                }

                                                // Handle CVM individual field changes
                                                if (sectionKey === 'cvm') {
                                                    // Check if this is individual field changes (like cvm[0].target_amount)
                                                    const hasArrayIndices = section.changes.some(change => /\[\d+\]/.test(change.field));

                                                    if (hasArrayIndices) {
                                                        // Group changes by array index
                                                        const groupedChanges: { [key: string]: LogChange[] } = {};

                                                        section.changes.forEach(change => {
                                                            const match = change.field.match(/\[(\d+)\]/);
                                                            if (match) {
                                                                const index = match[1];
                                                                const key = `cvm_${index}`;
                                                                if (!groupedChanges[key]) {
                                                                    groupedChanges[key] = [];
                                                                }
                                                                groupedChanges[key].push({
                                                                    ...change,
                                                                    field: change.field.replace(/\[\d+\]\./, '').replace(/^cvm\./, '').replace(/^targets\./, '') // Remove index and cvm/targets prefixes
                                                                });
                                                            }
                                                        });

                                                        return (
                                                            <div key={`${sectionKey}-after`} className="w-full mb-4">
                                                                <h3 className="text-base font-semibold text-gray-800 mb-3">CVM</h3>
                                                                {Object.entries(groupedChanges).map(([groupKey, groupChanges]) => {
                                                                    const itemIndex = groupKey.replace('cvm_', '');
                                                                    return (
                                                                        <div key={`${sectionKey}-${groupKey}-after`} className="w-full mb-4">
                                                                            <h4 className="text-sm font-semibold text-gray-700 mb-2">CVM Target ({Number(itemIndex) + 1})</h4>
                                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                                {groupChanges.map((change, index) => (
                                                                                    <div key={`${groupKey}-${index}-after`} className="w-full">
                                                                                        <h5 className="text-sm text-gray-600">{cleanFieldName(change.field)}</h5>
                                                                                        <div className="mt-1 font-medium text-gray-900" data-testid={`after-${cleanFieldName(change.field).toLowerCase().replace(/\s+/g, '-')}`}>
                                                                                            {formatValue(change.after_value, change.field)}
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        );
                                                    } else {
                                                        // Handle entire array changes (previous structure)
                                                        return (
                                                            <div key={`${sectionKey}-after`} className="w-full mb-4">
                                                                <h3 className="text-base font-semibold text-gray-800 mb-3">CVM</h3>
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                    {section.changes.map((change, index) => {
                                                                        // Check if this is targets array
                                                                        const isTargetsArray = change.field.includes('targets');
                                                                        
                                                                        if (isTargetsArray) {
                                                                            return (
                                                                                <div key={`${sectionKey}-${index}-after`} className="w-full">
                                                                                    <h4 className="text-sm font-semibold text-gray-700 mb-2">CVM Targets</h4>
                                                                                    <div className="w-full">
                                                                                        {formatValue(change.after_value, 'targets')}
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        } else {
                                                                            // Handle main CVM fields (from_date, to_date)
                                                                            return (
                                                                                <div key={`${sectionKey}-${index}-after`} className="w-full">
                                                                                    <h4 className="text-sm text-gray-600">{cleanFieldName(change.field)}</h4>
                                                                                    <div className="mt-1 font-medium text-gray-900" data-testid={`after-${cleanFieldName(change.field).toLowerCase().replace(/\s+/g, '-')}`}>
                                                                                        {formatValue(change.after_value, change.field)}
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        }
                                                                    })}
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                }

                                                // Regular rendering for other sections
                                                return section.changes.map((change, index) => (
                                                    <div key={`${sectionKey}-${index}-after`} className={`${change.field === 'business_assigned_area' ? 'w-full' : 'w-full sm:w-[30%]'}`}>
                                                        <h3 className="text-sm text-gray-600 capitalize">{cleanFieldName(change.field).replace(/_/g, ' ').replace(/\./g, ' ')}</h3>
                                                        <div className="mt-1 font-medium text-gray-900" data-testid={`after-${cleanFieldName(change.field).toLowerCase().replace(/\s+/g, '-')}`}>
                                                            {formatValue(change.after_value, change.field)}
                                                        </div>
                                                    </div>
                                                ))}
                                        )}
                                    </div>
                                </div >
                            </div >
                        )}
                    </div >
                </div >
            ) : (
                <Shimmer />
            )
            }

        </div >
    );
}
